import { sendNotification, useApiClient, useTranslation } from '@msdk/common'
import {
  Entity,
  EntityMetadata,
  EntityModel,
  OptionsetValue,
  readBaseMetadataByName,
  readEntityModelByName,
  readUserData,
  ServerOperationResult,
  StringValue,
} from '@msdk/definitions'
import { useEffect, useMemo, useState } from 'react'

import { CustomViewColumnDefinition } from '../context'
import { useColumnsLayoutComparer } from './useColumnsLayoutComparer'

type PersistedLayoutColumn = Pick<CustomViewColumnDefinition, 'id' | 'visibility'>

export function usePersistedColumnsLayout(viewName: string) {
  const client = useApiClient()
  const t = useTranslation()
  const hasSameColumnsLayout = useColumnsLayoutComparer()

  const [businessUnit, isAdmin] = useMemo(() => {
    const { business_unit, role } = readUserData()
    return [business_unit as string, !!role?.some((r) => r === 'app.admin')]
  }, [])

  const [isPersistedLayoutInit, setPersistedLayoutInit] = useState(false)
  const [persistedLayout, setPersistedLayout] = useState<PersistedLayoutColumn[] | undefined>(undefined)

  useEffect(() => {
    ;(async () => {
      const persisted = await getColumns(client, viewName, t)
      setPersistedLayout(persisted)
      setPersistedLayoutInit(true)
    })()
  }, [viewName])

  async function setPersistedColumnsLayout(columns: PersistedLayoutColumn[]) {
    const persistedEntity = await getPersistedColumnsLayout(client, viewName, t)
    const { baseMetatdata, entityModel } = getColumnsLayoutAPIInfo()

    try {
      let entityToPersist!: Entity

      if (!persistedEntity) {
        const createOperation = await client.entity.createEntity(
          baseMetatdata.msProvider,
          entityModel as EntityModel,
          businessUnit,
          undefined,
          undefined,
          {
            ViewName: new StringValue(viewName),
            Layout: mapToLayoutJsonValue(columns),
          },
        )
        if (createOperation.result == ServerOperationResult.Failure) throw new Error('save error')

        entityToPersist = createOperation.entities?.at(0) as Entity
      } else {
        entityToPersist = { ...persistedEntity }
        entityToPersist.fields['Layout'] = mapToLayoutJsonValue(columns)
      }

      const saveOperation = await client.entity.saveEntities(baseMetatdata.msProvider, entityModel as EntityModel, [
        entityToPersist as Entity,
      ])
      if (saveOperation.result == ServerOperationResult.Failure) throw new Error('save error')

      setPersistedLayout(columns)

      sendNotification({
        message: t('LBL_TABLE_MANAGEMENT_SET_AS_DEFAULT_LAYOUT_NOTIFY_SUCCESS', 'Default column layout set'),
        status: 'success',
      })
    } catch (e) {
      notifySaveColumnsLayoutError(t)
    }
  }

  async function removePersistedColumnsLayout() {
    const persistedEntity = await getPersistedColumnsLayout(client, viewName, t)
    if (!persistedEntity) {
      return
    }

    try {
      const { baseMetatdata, entityModel } = getColumnsLayoutAPIInfo()

      const entityToPersist: Entity = {
        ...persistedEntity,
      }
      entityToPersist.fields['ENTITY_STATUS'] = new OptionsetValue('INACTIVE', 'ENTITY_STATUS')
      const saveOperation = await client.entity.forceSaveEntities(
        baseMetatdata.msProvider,
        entityModel as EntityModel,
        [entityToPersist],
      )
      if (saveOperation.result == ServerOperationResult.Failure) throw new Error('save error')

      const deletedOperation = await client.entity.deleteEntity(
        baseMetatdata.msProvider,
        entityModel.name,
        persistedEntity?.id as string,
      )
      if (deletedOperation.result == ServerOperationResult.Failure) throw new Error('removing error')

      setPersistedLayout(undefined)
    } catch (e) {
      sendNotification({
        message: t(
          'LBL_TABLE_MANAGEMENT_SET_AS_DEFAULT_LAYOUT_REMOVE_NOTIFY_ERROR',
          'Removing default column layout error',
        ),
        status: 'error',
      })
    }
  }

  function syncToPersistedColumnsLayout(columns: CustomViewColumnDefinition[]): CustomViewColumnDefinition[] | false {
    try {
      const columnsLayout = persistedLayout

      if (!columnsLayout) {
        return false
      }

      const layoutMap = new Map(columnsLayout.map((col) => [col.id, col]))
      const reordered: CustomViewColumnDefinition[] = []
      const remaining: CustomViewColumnDefinition[] = []

      // First pass: add columns in layout order and apply visibility
      columnsLayout.forEach((layoutCol) => {
        const column = columns.find((col) => col.id === layoutCol.id)
        if (column) {
          reordered.push(
            column.visibility === layoutCol.visibility ? column : { ...column, visibility: layoutCol.visibility },
          )
        }
      })

      // Second pass: collect extra columns
      columns.forEach((column) => {
        if (!layoutMap.has(column.id)) {
          remaining.push(column)
        }
      })

      const syncedColumns = [...reordered, ...remaining]

      const isSameAsInput = hasSameColumnsLayout(columns, syncedColumns)

      return isSameAsInput ? false : syncedColumns
    } catch (e) {
      sendNotification({
        message: t(
          'LBL_TABLE_MANAGEMENT_SET_AS_DEFAULT_LAYOUT_RESTORE_NOTIFY_ERROR',
          'Restoring default column layout error',
        ),
        status: 'error',
      })
      return false
    }
  }

  function matchesPersistedColumnsLayout(columns: CustomViewColumnDefinition[]): boolean {
    const columnsLayout = persistedLayout
    if (!columnsLayout) {
      return false
    }
    return hasSameColumnsLayout(columns, columnsLayout)
  }

  return {
    isPersistedLayoutInit,
    persistedLayout,
    canPersistColumnsLayout: isAdmin,
    setPersistedColumnsLayout,
    removePersistedColumnsLayout,
    matchesPersistedColumnsLayout,
    syncToPersistedColumnsLayout,
  }
}

/**
 * Private methods
 */

async function getPersistedColumnsLayout(
  client: ReturnType<typeof useApiClient>,
  viewName: string,
  t: ReturnType<typeof useTranslation>,
) {
  try {
    const { baseMetatdata, entityModel } = getColumnsLayoutAPIInfo()

    const viewData = await client.entity.getView(
      baseMetatdata.msProvider,
      entityModel as EntityModel,
      'UI_TABLE_COLUMNS_LAYOUTS',
      {
        filters: {
          '@VIEW_NAME_FILTER': new StringValue(viewName),
        },
      },
    )
    return viewData?.source?.at(0)
  } catch (e) {
    notifyRestoreColumnsLayoutError(t)
  }
}

async function getColumns(
  client: ReturnType<typeof useApiClient>,
  viewName: string,
  t: ReturnType<typeof useTranslation>,
) {
  const persistedEntity = await getPersistedColumnsLayout(client, viewName, t)
  if (!persistedEntity) {
    return undefined
  }

  try {
    const fieldValue = persistedEntity.fields?.['Layout']?.value as string
    const columnsLayout: PersistedLayoutColumn[] = fieldValue ? JSON.parse(fieldValue) : []

    return columnsLayout.length ? columnsLayout : undefined
  } catch {
    notifyRestoreColumnsLayoutError(t)
    return undefined
  }
}

function notifySaveColumnsLayoutError(t: ReturnType<typeof useTranslation>) {
  sendNotification({
    message: t('LBL_TABLE_MANAGEMENT_SET_AS_DEFAULT_LAYOUT_NOTIFY_ERROR', 'Saving default column layout error'),
    status: 'error',
  })
}

function notifyRestoreColumnsLayoutError(t: ReturnType<typeof useTranslation>) {
  sendNotification({
    message: t(
      'LBL_TABLE_MANAGEMENT_SET_AS_DEFAULT_LAYOUT_RESTORE_NOTIFY_ERROR',
      'Restoring default column layout error',
    ),
    status: 'error',
  })
}

function mapToLayoutJsonValue(columns: PersistedLayoutColumn[]) {
  return new StringValue(JSON.stringify(columns.map(({ id, visibility }) => ({ id, visibility }))))
}

function getColumnsLayoutAPIInfo() {
  const baseMetatdata = readBaseMetadataByName('UI_TABLE_COLUMNS_LAYOUT') as EntityMetadata
  const entityModel = readEntityModelByName('UI_TABLE_COLUMNS_LAYOUT') as EntityModel

  return { baseMetatdata, entityModel }
}
